<script setup lang="ts">
import { useTemplateRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import { router } from '@/app/router';

interface Props {
  allGdprConsentsChecked: boolean;
  disableSkipButton: boolean;
  hasUploadedAndAnimatedPhotos: boolean;
  hasWebcamPhotos: boolean;
  showBackToWebcamButton: boolean;
}

interface Emits {
  cancelUploadClick: [];
  confirmClick: [];
  skipKioskPhotoClick: [];
}

interface PhotoFromPhoneFooterPublicApi {
  readonly continueButton: HTMLButtonElement | null;
  getContinueButton: () => HTMLButtonElement | null;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const props = defineProps<Props>();
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<Emits>();
const continueButton = useTemplateRef<HTMLButtonElement>('continueButton');

function getContinueButton(): HTMLButtonElement | null {
  return continueButton.value;
}

const publicApi: PhotoFromPhoneFooterPublicApi = {
  get continueButton(): HTMLButtonElement | null {
    return continueButton.value;
  },
  getContinueButton,
};

defineExpose(publicApi);
</script>

<template>
  <div class="PhotoFromPhone__footer" data-testid="photo-phone-footer">
    <template v-if="hasUploadedAndAnimatedPhotos">
      <template v-if="hasWebcamPhotos">
        <button
          class="PhotoFromPhone__footer-button-block kiosk-button kiosk-button--arrow-right"
          data-testid="photo-phone-continue"
          @click="emit('skipKioskPhotoClick')"
        >
          {{ $t('PhotoFromPhone.buttonContinue') || 'Continue' }}
        </button>
      </template>

      <template v-else>
        <button
          class="PhotoFromPhone__footer-button-inline kiosk-button-inline"
          data-testid="photo-phone-skip-kiosk-photo"
          @click="emit('skipKioskPhotoClick')"
        >
          {{
            $t('PhotoFromPhone.buttonSkipKioskPhoto') ||
            'Skip photo from kiosk'
          }}
        </button>

        <RouterLink
          class="PhotoFromPhone__footer-button-block kiosk-button kiosk-button--arrow-right"
          data-testid="photo-phone-continue"
          :to="
            router.getLinkToStep('photo-from-webcam', {
              query: { from: 'phone' },
            })
          "
        >
          {{
            $t('PhotoFromPhone.buttonTakeWebcamPhoto') ||
            'Take photo from webcam'
          }}
        </RouterLink>
      </template>
    </template>

    <template v-else>
      <RouterLink
        v-if="showBackToWebcamButton"
        class="PhotoFromPhone__footer-button-inline kiosk-button-inline kiosk-button-inline--arrow-left"
        :class="{ disabled: disableSkipButton }"
        :to="
          router.getLinkToStep('photo-from-webcam', {
            omitQuery: true,
          })
        "
      >
        {{ $t('PhotoFromPhone.buttonCancel') }}
      </RouterLink>

      <button
        v-if="!showBackToWebcamButton"
        class="PhotoFromPhone__footer-button-inline kiosk-button-inline"
        :class="{ disabled: disableSkipButton }"
        data-testid="photo-phone-skip-upload"
        @click="emit('cancelUploadClick')"
      >
        {{ $t('PhotoFromPhone.buttonSkip') }}
      </button>

      <button
        ref="continueButton"
        class="PhotoFromPhone__footer-button-block kiosk-button kiosk-button--arrow-right"
        :class="{
          disabled:
            !hasUploadedAndAnimatedPhotos || !allGdprConsentsChecked,
        }"
        data-testid="photo-phone-continue"
        @click="emit('confirmClick')"
      >
        {{ $t('PhotoFromPhone.buttonContinue') }}
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.PhotoFromPhone__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}

.PhotoFromPhone__footer-button-block,
.PhotoFromPhone__footer-button-inline {
  flex: 0 0 auto;
  box-sizing: border-box;
  width: auto;
  max-width: 100%;
  margin: 0;
  text-align: center;
}

.PhotoFromPhone__footer-button-inline {
  white-space: normal;
}

.PhotoFromPhone__footer > :only-child {
  flex-basis: 17rem;
  flex-grow: 0;
}

.PhotoFromPhone__footer-button-inline::before,
.PhotoFromPhone__footer-button-inline::after {
  display: var(--photo-from-phone-footer-decoration-display, inline);
}
</style>
